import Dexie, { Table } from "dexie";
import { action, makeObservable, observable, override } from "mobx";
import { DateTime } from "luxon";

import { MemCommonEditorContentUnderstandingInfo, assetProxyModule } from "@mem-labs/common-editor";

import { Maybe } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import { AppStore } from "@/store/AppStore";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import {
  SpaceAccountNoteAttachmentModelData,
  IndexedSpaceAccountNoteAttachmentSyncUpdateValue,
} from "@/store/note-attachments/types";
import { SpaceAccountNoteAttachmentObservable } from "@/store/note-attachments/SpaceAccountNoteAttachmentObservable";
import { SpaceAccountNoteAttachmentIndexes } from "@/store/note-attachments/SpaceAccountNoteAttachmentIndexes";
import { NoteAttachmentsObservable } from "@/store/note-attachments/NoteAttachmentsObservable";
import {
  extractFileIdFromAttachmentUrl,
  mapContentUnderstandingState,
} from "@/store/note-attachments/mapContentUnderstandingState";

export class AppStoreSpaceAccountNoteAttachmentsStore extends BaseSyncModelStore<
  SpaceAccountNoteAttachmentObservable,
  SpaceAccountNoteAttachmentModelData
> {
  private noteAttachmentObservables = new Map<Uuid, NoteAttachmentsObservable>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SpaceAccountNoteAttachment, ...injectedDeps });

    makeObservable<
      AppStoreSpaceAccountNoteAttachmentsStore,
      "noteAttachmentObservables" | "getOrCreateNoteAttachmentsObservable"
    >(this, {
      createSyncModel: true,
      remoteTable: override,
      localTable: override,
      computeIndexes: override,
      resetSync: override,
      getAttachmentsForNote: action,
      getContentUnderstandingForNote: false,
      countPdfPagesProcessedSince: false,
      noteAttachmentObservables: observable,
      getOrCreateNoteAttachmentsObservable: action,
    });
  }

  createSyncModel(
    data: SyncUpdateValue<SpaceAccountNoteAttachmentModelData>
  ): SpaceAccountNoteAttachmentObservable {
    return new SpaceAccountNoteAttachmentObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public get remoteTable() {
    return this.db.mappedTables[this.modelKind].remote as Table<
      SyncUpdateValue<SpaceAccountNoteAttachmentModelData>
    >;
  }

  public get localTable() {
    return this.db.mappedTables[this.modelKind]
      .local as Table<IndexedSpaceAccountNoteAttachmentSyncUpdateValue>;
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<SpaceAccountNoteAttachmentModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<SpaceAccountNoteAttachmentModelData>[];
  }): Record<string, unknown> {
    return new SpaceAccountNoteAttachmentIndexes({ remoteData, optimisticUpdates }).indexes;
  }

  getAttachmentsForNote(noteId: Uuid): NoteAttachmentsObservable {
    return this.getOrCreateNoteAttachmentsObservable(noteId);
  }

  async getContentUnderstandingForNote(
    noteId: Uuid,
    { includeDisplayState }: { includeDisplayState?: boolean } = {}
  ): Promise<Record<string, MemCommonEditorContentUnderstandingInfo>> {
    const result: Record<string, MemCommonEditorContentUnderstandingInfo> = {};

    const records = await this.localTable.where("associated_note_id").equals(noteId).toArray();

    for (const record of records) {
      const attachmentUrl = record.model_data.attachment_url;
      if (!attachmentUrl) continue;

      const cuInfo = mapContentUnderstandingState({
        extractionStatus: record.model_data.extraction_status,
        indexingStatus: record.model_data.indexing_status,
        processingInfo: record.model_data.processing_info,
        mediaKind: record.model_data.mime_type?.startsWith("image/") ? "image" : "file",
        suggestedSearch: record.model_data.suggested_search,
        ocrMarkdown: record.model_data.ocr_markdown,
        includeDisplayState,
      });
      if (!cuInfo) continue;

      // Primary key: UUID from the attachment URL (upload-time convention).
      const fileId = extractFileIdFromAttachmentUrl(attachmentUrl);
      if (fileId) result[fileId] = cuInfo;

      // Secondary key: unwrapped attachment URL. Mirror the reaction in
      // mem-common-editor-store's CU dispatch — the seed path at note-boot
      // replaces `editor.storage.file.contentUnderstandingMap`, so this map
      // must also dual-key or the URL fallback gets wiped for old nodes.
      const unwrappedUrl =
        assetProxyModule.extractOriginalUrlFromProxy({ proxiedUrl: attachmentUrl }) ??
        attachmentUrl;
      result[unwrappedUrl] = cuInfo;
    }

    return result;
  }

  async countPdfPagesProcessedSince(periodStart: DateTime): Promise<number> {
    const periodStartISO = periodStart.toISO();
    const compoundIndex = "[mime_type+created_at+pages_processed+model_id]";

    // mime_type is pinned to "application/pdf" so non-PDF attachments (images
    // also receive a pages_processed value) are excluded from the PDF-page
    // paywall counter. .keys() reads only the index B-tree — no full record
    // reads.
    const keys = await this.localTable
      .where(compoundIndex)
      .between(
        ["application/pdf", periodStartISO, Dexie.minKey, Dexie.minKey],
        ["application/pdf", Dexie.maxKey, Dexie.maxKey, Dexie.maxKey]
      )
      .keys();

    // Each key is a tuple: [mime_type, created_at, pages_processed, model_id]
    return (keys as unknown as [string, string, number, string][]).reduce(
      (total, tuple) => total + (tuple[2] ?? 0),
      0
    );
  }

  override resetSync(): void {
    super.resetSync();
    this.noteAttachmentObservables.forEach(obs => obs.dispose());
    this.noteAttachmentObservables.clear();
  }

  private getOrCreateNoteAttachmentsObservable(noteId: Uuid): NoteAttachmentsObservable {
    let obs = this.noteAttachmentObservables.get(noteId);

    if (!obs) {
      obs = new NoteAttachmentsObservable({ store: this, noteId });
      this.noteAttachmentObservables.set(noteId, obs);
    }

    return obs;
  }
}
